import React, { useEffect, useState } from "react";
import { Edit, MoreHorizontal, Lock, Unlock, Power, KeyRound, Trash2, UserPlus, History } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/layout/PageHeader";
import DataTable from "@/components/common/DataTable";
import StatusBadge from "@/components/common/StatusBadge";
import ConfirmDialog from "@/components/common/ConfirmDialog";
import { StickyActionBar } from "@/components/common/FormSection";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { api, formatApiError } from "@/lib/api";
import UserFormFields from "@/components/users/UserFormFields";
import UserCommsSheet from "@/components/users/UserCommsSheet";

const CLINICAL_ROLE_CODES = ["physician", "hah_physician", "nurse", "hah_nurse"];

const EMPTY_USER = {
  email: "",
  first_name: "",
  second_name: "",
  last_name: "",
  gender: "",
  dob: "",
  years_of_experience: "",
  dial_code: "",
  phone: "",
  alternate_dial_code: "",
  alternate_phone: "",
  title: "",
  role_ids: [],
  facility_ids: [],
  department: "",
  status: "active",
  nationality: "",
  identity_type: "",
  identity_number: "",
  license_number: "",
  license_expiry_date: "",
  specialty_type: "",
  medical_specialization: "",
  sub_specialty: "",
  medical_specializations: [],
  sub_specialties: [],
  password: "Welcome@123",
};

export default function UsersPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(15);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortBy, setSortBy] = useState("created_at");
  const [sortDir, setSortDir] = useState(-1);

  const [roles, setRoles] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [lookups, setLookups] = useState({}); // { nationality:[], identity_type:[], specialty_type:[], medical_specialization:[], sub_specialty:[] }

  const [sheetOpen, setSheetOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_USER);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [saving, setSaving] = useState(false);

  const [confirm, setConfirm] = useState({ open: false });
  const [commsSheet, setCommsSheet] = useState({ open: false, user: null });

  async function load() {
    setLoading(true);
    try {
      const params = { page, page_size: pageSize, sort_by: sortBy, sort_dir: sortDir };
      if (search) params.q = search;
      if (statusFilter !== "all") params.status = statusFilter;
      const res = await api.get("/users", { params });
      setItems(res.data.items);
      setTotal(res.data.total);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [page, sortBy, sortDir, statusFilter]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { const t = setTimeout(() => { setPage(1); load(); }, 300); return () => clearTimeout(t); }, [search]);

  useEffect(() => {
    api.get("/roles", { params: { page_size: 50 } }).then((r) => setRoles(r.data.items)).catch(() => {});
    api.get("/facilities", { params: { page_size: 50 } }).then((r) => setFacilities(r.data.items)).catch(() => {});
    const codes = ["nationality", "identity_type", "specialty_type", "medical_specialization", "sub_specialty"];
    Promise.all(codes.map((c) => api.get(`/forms/lookup-options/${c}`).then((r) => [c, r.data.items]).catch(() => [c, []])))
      .then((pairs) => setLookups(Object.fromEntries(pairs)));
  }, []);

  // Derived: codes of currently selected roles + whether any is clinical
  const selectedRoleCodes = (form.role_ids || [])
    .map((id) => roles.find((r) => r.id === id)?.code)
    .filter(Boolean);
  const isClinical = selectedRoleCodes.some((c) => CLINICAL_ROLE_CODES.includes(c));
  const isSaudi = (form.nationality || "").toLowerCase() === "saudi";
  // Sub-specialties cascade off the selected medical specializations (union of
  // all currently-checked medical specializations).
  const selectedMedSpecs = new Set(form.medical_specializations || []);
  const subSpecialtyOpts = (lookups.sub_specialty || []).filter(
    (o) => !o.parent_value || selectedMedSpecs.has(o.parent_value)
  );

  // Pure validator — returns a full map of field -> message
  function computeErrors(f) {
    const codes = (f.role_ids || []).map((id) => roles.find((r) => r.id === id)?.code).filter(Boolean);
    const clinical = codes.some((c) => CLINICAL_ROLE_CODES.includes(c));
    const saudi = (f.nationality || "").toLowerCase() === "saudi";
    const e = {};
    if (!f.first_name) e.first_name = "First name is required";
    if (!f.last_name) e.last_name = "Last name is required";
    if (!f.email) e.email = "Email is required";
    else if (!/^\S+@\S+\.\S+$/.test(f.email)) e.email = "Enter a valid email address";
    if (!f.gender) e.gender = "Gender is required";
    if (!f.dob) e.dob = "Date of birth is required";
    else if (f.dob > new Date().toISOString().slice(0, 10)) e.dob = "Date of birth cannot be in the future";
    if (!f.nationality) e.nationality = "Nationality is required";
    if (f.years_of_experience === "" || f.years_of_experience === null || f.years_of_experience === undefined) e.years_of_experience = "Years of experience is required";
    else if (!/^\d{1,2}$/.test(String(f.years_of_experience)) || Number(f.years_of_experience) > 70) e.years_of_experience = "Enter a whole number between 0 and 70";
    if (f.phone && !f.dial_code) e.dial_code = "Select a dial code before entering the phone number";
    if (f.alternate_phone && !f.alternate_dial_code) e.alternate_dial_code = "Select a dial code for the alternate number";
    if (!f.role_ids || f.role_ids.length === 0) e.role_ids = "At least one role is required";

    if (f.identity_number && !f.identity_type) e.identity_type = "Select identity type before entering an identity number";
    if (saudi) {
      if (f.identity_type && f.identity_type !== "National Number") e.identity_type = "Saudi nationals must use 'National Number'";
      if (f.identity_type === "National Number" && f.identity_number && !/^1\d{9}$/.test(f.identity_number)) e.identity_number = "Saudi National Number must start with 1 and be exactly 10 digits";
    }

    if (clinical) {
      if (!f.license_number) e.license_number = "License number is required for physicians and nurses";
      if (!f.license_expiry_date) e.license_expiry_date = "License expiry date is required";
      if (!f.specialty_type) e.specialty_type = "Specialty type is required";
      if (!(f.medical_specializations || []).length) e.medical_specializations = "Select at least one medical specialization";
      if (!(f.sub_specialties || []).length) e.sub_specialties = "Select at least one sub specialty";
    }
    return e;
  }

  // Live validation: recompute on every change; only surface errors for fields
  // the user has already touched (or everything once a save was attempted).
  useEffect(() => {
    if (!sheetOpen) return;
    const all = computeErrors(form);
    const visible = submitAttempted
      ? all
      : Object.fromEntries(Object.entries(all).filter(([k]) => touched[k]));
    setErrors((prev) => {
      // preserve server-side errors (e.g. uniqueness 409) that aren't recomputed here
      const merged = { ...visible };
      for (const k of ["license_number", "identity_number", "email"]) {
        if (prev[k] && !all[k] && prev[k].includes("already in use")) merged[k] = prev[k];
      }
      return merged;
    });
    // eslint-disable-next-line
  }, [form, roles, touched, submitAttempted, sheetOpen]);

  function setField(name, value) {
    setForm((f) => ({ ...f, [name]: value }));
    setTouched((t) => ({ ...t, [name]: true }));
  }

  function toggleArrayItem(field, value) {
    setForm((f) => {
      const arr = f[field] || [];
      return { ...f, [field]: arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value] };
    });
    setTouched((t) => ({ ...t, [field]: true }));
  }

  function openCreate() {
    setEditing(null);
    setForm(EMPTY_USER);
    setErrors({});
    setTouched({});
    setSubmitAttempted(false);
    setSheetOpen(true);
  }
  function openEdit(row) {
    setEditing(row);
    setForm({
      email: row.email, first_name: row.first_name, second_name: row.second_name || "", last_name: row.last_name,
      gender: row.gender || "", dob: row.dob ? String(row.dob).slice(0, 10) : "",
      years_of_experience: row.years_of_experience ?? "",
      dial_code: row.dial_code || "", phone: row.phone || "",
      alternate_dial_code: row.alternate_dial_code || "", alternate_phone: row.alternate_phone || "",
      title: row.title || "",
      role_ids: row.role_ids && row.role_ids.length ? row.role_ids : (row.role_id ? [row.role_id] : []),
      facility_ids: row.facility_ids || [], department: row.department || "",
      status: row.status || "active",
      nationality: row.nationality || "", identity_type: row.identity_type || "",
      identity_number: row.identity_number || "",
      license_number: row.license_number || "", license_expiry_date: row.license_expiry_date ? String(row.license_expiry_date).slice(0, 10) : "",
      specialty_type: row.specialty_type || "",
      medical_specialization: row.medical_specialization || "",
      sub_specialty: row.sub_specialty || "",
      medical_specializations: (row.medical_specializations && row.medical_specializations.length)
        ? row.medical_specializations
        : (row.medical_specialization ? [row.medical_specialization] : []),
      sub_specialties: (row.sub_specialties && row.sub_specialties.length)
        ? row.sub_specialties
        : (row.sub_specialty ? [row.sub_specialty] : []),
      password: "",
    });
    setErrors({});
    setTouched({});
    setSubmitAttempted(false);
    setSheetOpen(true);
  }

  function validate() {
    setSubmitAttempted(true);
    const e = computeErrors(form);
    setErrors(e);
    if (Object.keys(e).length > 0) toast.error("Please fix the highlighted fields before saving.");
    return Object.keys(e).length === 0;
  }

  async function save() {
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = { ...form };
      payload.years_of_experience = payload.years_of_experience === "" ? null : Number(payload.years_of_experience);
      if (editing) {
        delete payload.password;
        await api.put(`/users/${editing.id}`, payload);
        toast.success("User updated");
      } else {
        await api.post("/users", payload);
        toast.success("User created");
      }
      setSheetOpen(false);
      load();
    } catch (e) {
      const fe = e?.response?.data?.detail?.field_errors;
      if (fe && typeof fe === "object") setErrors((prev) => ({ ...prev, ...fe }));
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  }

  async function doAction(row, action) {
    try {
      if (action === "delete") {
        await api.delete(`/users/${row.id}`);
        toast.success("User deleted");
      } else if (action === "reset") {
        const res = await api.post(`/users/${row.id}/reset-password`);
        toast.success(`Password reset to: ${res.data.temporary_password}`);
      } else {
        await api.post(`/users/${row.id}/${action}`);
        toast.success(`User ${action}d`);
      }
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  const columns = [
    {
      key: "name", label: "Name", sortable: false,
      render: (r) => (
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-[#EAF8FA] text-[#0B7E8C] flex items-center justify-center text-xs font-semibold">
            {(r.first_name?.[0] || "?") + (r.last_name?.[0] || "")}
          </div>
          <div className="min-w-0">
            <div className="font-medium text-[#1F2937] truncate">{r.first_name} {r.last_name}</div>
            <div className="text-xs text-[#6B7280] truncate">{r.email}</div>
          </div>
        </div>
      ),
    },
    { key: "title", label: "Title", sortable: false, render: (r) => r.title || "—" },
    {
      key: "role_code", label: "Roles", sortable: true,
      render: (r) => {
        const codes = (r.role_codes && r.role_codes.length ? r.role_codes : (r.role_code ? [r.role_code] : []));
        if (codes.length === 0) return <span className="text-sm text-[#6B7280]">—</span>;
        return (
          <div className="flex flex-wrap gap-1">
            {codes.map((c) => (
              <span key={c} className="text-xs capitalize text-[#1F2937] bg-[#F1F5F9] px-1.5 py-0.5 rounded">{(c || "").replace(/_/g, " ")}</span>
            ))}
          </div>
        );
      },
    },
    { key: "department", label: "Department", sortable: false, render: (r) => r.department || "—" },
    {
      key: "facility_ids", label: "Facilities", sortable: false,
      render: (r) => <span className="text-xs text-[#6B7280]">{(r.facility_ids || []).length} assigned</span>,
    },
    {
      key: "status", label: "Status", sortable: true,
      render: (r) => <StatusBadge status={r.status} testId={`user-status-${r.id}`} />,
    },
    {
      key: "actions", label: "", sortable: false,
      render: (r) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid={`user-actions-${r.id}`} className="w-8 h-8 rounded-md hover:bg-[#F5F7FA] flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
              <MoreHorizontal className="w-4 h-4 text-[#6B7280]" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-44">
            <DropdownMenuItem onClick={() => openEdit(r)} data-testid={`user-edit-${r.id}`}>
              <Edit className="w-4 h-4 mr-2" /> Edit
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setCommsSheet({ open: true, user: r })} data-testid={`user-comms-${r.id}`}>
              <History className="w-4 h-4 mr-2" /> Communications
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => doAction(r, "reset")} data-testid={`user-reset-${r.id}`}>
              <KeyRound className="w-4 h-4 mr-2" /> Reset password
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            {r.status !== "active" && (
              <DropdownMenuItem onClick={() => doAction(r, "activate")}>
                <Power className="w-4 h-4 mr-2" /> Activate
              </DropdownMenuItem>
            )}
            {r.status === "active" && (
              <DropdownMenuItem onClick={() => doAction(r, "deactivate")}>
                <Power className="w-4 h-4 mr-2" /> Deactivate
              </DropdownMenuItem>
            )}
            {r.status !== "locked" ? (
              <DropdownMenuItem onClick={() => doAction(r, "lock")}>
                <Lock className="w-4 h-4 mr-2" /> Lock
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem onClick={() => doAction(r, "unlock")}>
                <Unlock className="w-4 h-4 mr-2" /> Unlock
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => setConfirm({ open: true, title: "Delete user", description: `Soft-delete ${r.email}?`, onConfirm: () => doAction(r, "delete"), destructive: true })}
              data-testid={`user-delete-${r.id}`}
              className="text-[#EF4444]"
            >
              <Trash2 className="w-4 h-4 mr-2" /> Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  return (
    <div data-testid="users-page">
      <PageHeader
        title="User Management"
        subtitle="Manage clinicians, administrators, and staff across the H@H network."
        actions={
          <Button
            onClick={openCreate}
            data-testid="create-user-button"
            className="bg-[#0B7E8C] hover:bg-[#128C97] text-white"
          >
            <UserPlus className="w-4 h-4 mr-1.5" /> New User
          </Button>
        }
      />

      <DataTable
        testId="users-table"
        columns={columns}
        data={items}
        total={total}
        page={page}
        pageSize={pageSize}
        loading={loading}
        searchValue={search}
        onSearchChange={setSearch}
        onPageChange={setPage}
        onSortChange={(by, dir) => { setSortBy(by); setSortDir(dir); }}
        sortBy={sortBy}
        sortDir={sortDir}
        exportFilename="users.csv"
        filters={
          <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
            <SelectTrigger className="w-[150px] h-9 border-[#E5E7EB]" data-testid="user-status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
              <SelectItem value="locked">Locked</SelectItem>
            </SelectContent>
          </Select>
        }
      />

      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent side="right" className="w-full sm:max-w-[640px] overflow-y-auto p-0 bg-[#FAFBFC]">
          <SheetHeader className="px-6 py-5 border-b border-[#E5E7EB] bg-white">
            <SheetTitle>{editing ? "Edit user" : "Create user"}</SheetTitle>
            <SheetDescription>
              {editing ? "Update the user profile, role and assignments." : "Add a new user to the platform."}
            </SheetDescription>
          </SheetHeader>

          <UserFormFields
            form={form}
            setForm={setForm}
            setField={setField}
            setTouched={setTouched}
            toggleArrayItem={toggleArrayItem}
            errors={errors}
            editing={editing}
            roles={roles}
            facilities={facilities}
            lookups={lookups}
            isClinical={isClinical}
            isSaudi={isSaudi}
            subSpecialtyOpts={subSpecialtyOpts}
          />

          <StickyActionBar>
            <Button variant="outline" onClick={() => setSheetOpen(false)} data-testid="user-form-cancel" className="border-[#E5E7EB]">Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="user-form-save" className="bg-[#0B7E8C] hover:bg-[#128C97] text-white">
              {saving ? "Saving…" : editing ? "Save changes" : "Create user"}
            </Button>
          </StickyActionBar>
        </SheetContent>
      </Sheet>

      <ConfirmDialog
        open={confirm.open}
        onOpenChange={(o) => setConfirm({ ...confirm, open: o })}
        title={confirm.title}
        description={confirm.description}
        onConfirm={() => { confirm.onConfirm?.(); setConfirm({ open: false }); }}
        destructive={confirm.destructive}
      />

      <UserCommsSheet
        open={commsSheet.open}
        onOpenChange={(o) => setCommsSheet((s) => ({ ...s, open: o }))}
        user={commsSheet.user}
      />
    </div>
  );
}
